import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  CoffeeOutlined,
  CreditCardOutlined,
  DownloadOutlined,
  FallOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  RiseOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { InvoiceNotification } from '../types/invoiceNotification';
import { exportStatisticsToExcel } from '../utils/invoiceStatisticsExcelUtils';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

// ─── Design tokens ────────────────────────────────────────────────────────────
const PRIMARY  = '#1890ff';
const SUCCESS  = '#52c41a';
const WARNING  = '#fa8c16';
const DANGER   = '#ff4d4f';
const MUTED    = '#8c8c8c';
const BORDER   = '#f0f0f0';
const BG_LIGHT = '#fafafa';
const RANK_COLORS = [PRIMARY, '#40a9ff', '#69c0ff', '#91d5ff', '#bae7ff', '#d6f0ff'];
const PAGE_SIZE = 10;

// ─── Types ────────────────────────────────────────────────────────────────────
interface Props { invoices: InvoiceNotification[] }

interface ItemStat {
  itemName: string;
  totalQty: number;
  totalRevenue: number;
  invoiceCount: number;
  options: Record<string, { name: string; qty: number; revenue: number }>;
}

interface OptionStat {
  optionName: string;
  itemName: string;
  totalQty: number;
  totalRevenue: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseVnDateTime = (date?: string, time?: string): Dayjs | null => {
  if (!date || date === '-') return null;
  const t = time && time !== '-' ? time.trim() : '00:00';
  const d1 = dayjs(`${date.trim()} ${t}`, 'DD/MM/YYYY HH:mm:ss', true);
  if (d1.isValid()) return d1;
  const d2 = dayjs(`${date.trim()} ${t}`, 'DD/MM/YYYY HH:mm', true);
  return d2.isValid() ? d2 : null;
};

const fmtMoney = (v: number) => `${v.toLocaleString('vi-VN')} ₫`;

// ─── Hook: Infinity scroll ────────────────────────────────────────────────────
function useInfiniteList<T>(allItems: T[], pageSize = PAGE_SIZE) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => { setVisibleCount(pageSize); }, [allItems, pageSize]);

  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + pageSize, allItems.length));
  }, [allItems.length, pageSize]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [loadMore]);

  return {
    visibleItems: allItems.slice(0, visibleCount),
    hasMore: visibleCount < allItems.length,
    sentinelRef,
    remaining: allItems.length - visibleCount,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const SectionTitle: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <Space size={6}>
    <span style={{ color: PRIMARY, fontSize: 15 }}>{icon}</span>
    <Text strong style={{ fontSize: 14, color: '#262626' }}>{text}</Text>
  </Space>
);

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const bg = rank === 1 ? PRIMARY : rank === 2 ? '#40a9ff' : rank === 3 ? '#69c0ff' : BG_LIGHT;
  const color = rank <= 3 ? '#fff' : MUTED;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 22, height: 22, borderRadius: '50%',
      background: bg, color, fontSize: 11, fontWeight: 700, flexShrink: 0,
      border: rank <= 3 ? 'none' : `1px solid ${BORDER}`,
    }}>
      {rank}
    </span>
  );
};

// ─── BarRow — có thể click ────────────────────────────────────────────────────
interface BarRowProps {
  rank: number;
  label: string;
  value: number;
  maxValue: number;
  meta: string;
  strokeColor?: string;
  badgeText?: string;
  badgeColor?: string;
  onClick?: () => void;
}

const BarRow: React.FC<BarRowProps> = ({
  rank, label, value, maxValue, meta,
  strokeColor = PRIMARY, badgeText, badgeColor = 'blue', onClick,
}) => (
  <div
    onClick={onClick}
    style={{
      marginBottom: 10,
      cursor: onClick ? 'pointer' : 'default',
      borderRadius: 6,
      padding: '6px 8px',
      margin: '0 -8px 6px',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLDivElement).style.background = BG_LIGHT; }}
    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
      <Space size={8} style={{ flex: 1, minWidth: 0 }}>
        <RankBadge rank={rank} />
        <Text
          strong
          style={{ fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={label}
        >
          {label}
        </Text>
        {badgeText && <Tag color={badgeColor} style={{ margin: 0, fontSize: 11 }}>{badgeText}</Tag>}
      </Space>
      <Space size={10} style={{ flexShrink: 0, marginLeft: 8 }}>
        <Text strong style={{ fontSize: 13, color: strokeColor }}>{value} lượt</Text>
        <Text style={{ fontSize: 12, color: MUTED }}>{meta}</Text>
        {onClick && (
          <Tooltip title="Xem chi tiết">
            <InfoCircleOutlined style={{ color: MUTED, fontSize: 13 }} />
          </Tooltip>
        )}
      </Space>
    </div>
    <Progress
      percent={maxValue > 0 ? Math.round((value / maxValue) * 100) : 0}
      showInfo={false}
      strokeColor={strokeColor}
      trailColor={BORDER}
      size="small"
    />
  </div>
);

// ─── Modal chi tiết món ───────────────────────────────────────────────────────
interface ItemDetailModalProps {
  item: ItemStat | null;
  allItemsCount: number;          // tổng số món trong danh sách để tính %
  maxQtyInList: number;
  onClose: () => void;
}

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({ item, allItemsCount, maxQtyInList, onClose }) => {
  if (!item) return null;

  const optionList = Object.values(item.options).sort((a, b) => b.qty - a.qty);
  const maxOptQty  = optionList[0]?.qty || 1;
  const avgPerCall = item.totalQty > 0 ? Math.round(item.totalRevenue / item.totalQty) : 0;
  const shareOfTop = maxQtyInList > 0 ? Math.round((item.totalQty / maxQtyInList) * 100) : 0;

  return (
    <Modal
      open={!!item}
      onCancel={onClose}
      footer={null}
      width={640}
      title={
        <Space size={10}>
          <CoffeeOutlined style={{ color: PRIMARY }} />
          <span style={{ fontSize: 16, fontWeight: 700 }}>{item.itemName}</span>
        </Space>
      }
      styles={{ body: { padding: '16px 24px 24px' } }}
    >
      {/* KPI tóm tắt */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {[
          { label: 'Tổng lượt gọi',    value: item.totalQty,     color: PRIMARY,  fmt: undefined },
          { label: 'Doanh thu',        value: item.totalRevenue, color: SUCCESS,  fmt: (v: any) => Number(v).toLocaleString('vi-VN'), suffix: '₫' },
          { label: 'TB / lần gọi',     value: avgPerCall,        color: WARNING,  fmt: (v: any) => Number(v).toLocaleString('vi-VN'), suffix: '₫' },
          { label: 'Xuất hiện trong HĐ', value: item.invoiceCount, color: MUTED, fmt: undefined },
        ].map(k => (
          <Col span={12} key={k.label}>
            <div style={{ background: BG_LIGHT, borderRadius: 8, padding: '12px 14px', border: `1px solid ${BORDER}` }}>
              <Text style={{ fontSize: 12, color: MUTED, display: 'block', marginBottom: 4 }}>{k.label}</Text>
              <Statistic
                value={k.value}
                formatter={k.fmt as any}
                suffix={(k as any).suffix}
                valueStyle={{ color: k.color, fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}
              />
            </div>
          </Col>
        ))}
      </Row>

      {/* So sánh với món gọi nhiều nhất */}
      <div style={{ marginBottom: 20 }}>
        <Text style={{ fontSize: 12, color: MUTED, display: 'block', marginBottom: 6 }}>
          Tỷ lệ so với món được gọi nhiều nhất
        </Text>
        <Progress
          percent={shareOfTop}
          strokeColor={shareOfTop >= 70 ? DANGER : shareOfTop >= 40 ? PRIMARY : MUTED}
          trailColor={BORDER}
          format={p => <Text style={{ fontSize: 13, fontWeight: 600 }}>{p}%</Text>}
        />
      </div>

      <Divider style={{ margin: '0 0 16px' }} />

      {/* Bảng option */}
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong style={{ fontSize: 13 }}>
          Option ({optionList.length > 0 ? `${optionList.length} loại` : 'không có'})
        </Text>
        {optionList.length > 0 && (
          <Space size={8}>
            <Tag color="blue" style={{ fontSize: 11 }}>Phổ biến nhất: {optionList[0].name}</Tag>
            {optionList.length > 1 && (
              <Tag style={{ fontSize: 11 }}>Ít nhất: {optionList[optionList.length - 1].name}</Tag>
            )}
          </Space>
        )}
      </div>

      {optionList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px 0', background: BG_LIGHT, borderRadius: 8, border: `1px solid ${BORDER}` }}>
          <Text type="secondary" style={{ fontSize: 13 }}>Món này không có option nào</Text>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {optionList.map((opt, idx) => (
            <div
              key={opt.name}
              style={{
                padding: '10px 12px',
                borderRadius: 6,
                background: idx === 0 ? `rgba(24,144,255,0.04)` : 'transparent',
                border: idx === 0 ? `1px solid rgba(24,144,255,0.18)` : `1px solid transparent`,
                marginBottom: 6,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <Space size={8}>
                  <RankBadge rank={idx + 1} />
                  <Text strong style={{ fontSize: 13 }}>{opt.name}</Text>
                  {idx === 0 && <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>Phổ biến nhất</Tag>}
                  {idx === optionList.length - 1 && optionList.length > 1 && (
                    <Tag style={{ margin: 0, fontSize: 11 }}>Ít nhất</Tag>
                  )}
                </Space>
                <Space size={14}>
                  <Text strong style={{ fontSize: 13, color: PRIMARY }}>{opt.qty} lượt</Text>
                  <Text style={{ fontSize: 12, color: MUTED }}>{fmtMoney(opt.revenue)}</Text>
                </Space>
              </div>
              <Progress
                percent={Math.round((opt.qty / maxOptQty) * 100)}
                showInfo={false}
                strokeColor={idx === 0 ? PRIMARY : '#91d5ff'}
                trailColor={BORDER}
                size="small"
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

// ─── InfiniteBarList ──────────────────────────────────────────────────────────
interface InfiniteBarListProps {
  allItems: ItemStat[];
  maxQty: number;
  strokeColor: string;
  firstBadgeText: string;
  firstBadgeColor: string;
  height?: number;
  onItemClick: (item: ItemStat) => void;
}

const InfiniteBarList: React.FC<InfiniteBarListProps> = ({
  allItems, maxQty, strokeColor, firstBadgeText, firstBadgeColor, height = 420, onItemClick,
}) => {
  const { visibleItems, hasMore, sentinelRef, remaining } = useInfiniteList(allItems);

  if (allItems.length === 0) {
    return <Text type="secondary" style={{ fontSize: 13 }}>Không có dữ liệu</Text>;
  }

  return (
    <div style={{ maxHeight: height, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
      {visibleItems.map((item, idx) => (
        <BarRow
          key={item.itemName}
          rank={idx + 1}
          label={item.itemName}
          value={item.totalQty}
          maxValue={maxQty}
          meta={fmtMoney(item.totalRevenue)}
          strokeColor={idx === 0 ? strokeColor : PRIMARY}
          badgeText={idx === 0 ? firstBadgeText : undefined}
          badgeColor={firstBadgeColor}
          onClick={() => onItemClick(item)}
        />
      ))}

      <div ref={sentinelRef} style={{ height: 1 }} />

      {hasMore && (
        <div style={{ textAlign: 'center', padding: '8px 0 4px', borderTop: `1px solid ${BORDER}`, marginTop: 4 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 14, color: PRIMARY }} spin />} />
          <Text style={{ fontSize: 12, color: MUTED, marginLeft: 8 }}>
            Đang tải thêm {Math.min(PAGE_SIZE, remaining)} / còn {remaining} món...
          </Text>
        </div>
      )}

      {!hasMore && allItems.length > PAGE_SIZE && (
        <div style={{ textAlign: 'center', padding: '6px 0 2px', borderTop: `1px solid ${BORDER}`, marginTop: 4 }}>
          <Text style={{ fontSize: 12, color: MUTED }}>Đã hiển thị tất cả {allItems.length} món</Text>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const InvoiceStatistics: React.FC<Props> = ({ invoices }) => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [detailItem, setDetailItem] = useState<ItemStat | null>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!dateRange || (!dateRange[0] && !dateRange[1])) return invoices;
    const [start, end] = dateRange;
    return invoices.filter(inv => {
      const dt = parseVnDateTime(inv.checkInDate, inv.checkInTime);
      if (!dt) return false;
      return (start ? !dt.isBefore(start.startOf('day')) : true)
          && (end   ? !dt.isAfter(end.endOf('day'))       : true);
    });
  }, [invoices, dateRange]);

  const uniqueInvoices = useMemo(() => {
    const map = new Map<string, InvoiceNotification[]>();
    filtered.forEach(inv => {
      const key = inv.orderCode || inv.invoiceCode;
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(inv);
    });
    return map;
  }, [filtered]);

  const itemStatsByMost = useMemo(() => {
    const map = new Map<string, ItemStat>();
    filtered.forEach(inv => {
      const name = (inv.itemName || '').trim();
      if (!name) return;
      if (!map.has(name)) {
        map.set(name, { itemName: name, totalQty: 0, totalRevenue: 0, invoiceCount: 0, options: {} });
      }
      const s = map.get(name)!;
      s.totalQty     += inv.quantity || 0;
      s.totalRevenue += inv.lineTotal || 0;
      s.invoiceCount += 1;
      (inv.selectedOptions || []).forEach(opt => {
        const oName = (opt.optionName || '').trim();
        if (!oName) return;
        if (!s.options[oName]) s.options[oName] = { name: oName, qty: 0, revenue: 0 };
        s.options[oName].qty     += opt.quantity || 0;
        s.options[oName].revenue += opt.amount || 0;
      });
    });
    return Array.from(map.values()).sort((a, b) => b.totalQty - a.totalQty);
  }, [filtered]);

  const itemStatsByLeast = useMemo(
    () => [...itemStatsByMost].sort((a, b) => a.totalQty - b.totalQty),
    [itemStatsByMost]
  );

  const maxQty       = itemStatsByMost[0]?.totalQty || 1;
  const bottomMaxQty = itemStatsByLeast[itemStatsByLeast.length - 1]?.totalQty || 1;

  const optionStats = useMemo(() => {
    const map = new Map<string, OptionStat>();
    filtered.forEach(inv => {
      const itemName = (inv.itemName || '').trim();
      (inv.selectedOptions || []).forEach(opt => {
        const oName = (opt.optionName || '').trim();
        if (!oName) return;
        const key = `${itemName}__${oName}`;
        if (!map.has(key)) map.set(key, { optionName: oName, itemName, totalQty: 0, totalRevenue: 0 });
        const s = map.get(key)!;
        s.totalQty     += opt.quantity || 0;
        s.totalRevenue += opt.amount || 0;
      });
    });
    return Array.from(map.values()).sort((a, b) => b.totalQty - a.totalQty);
  }, [filtered]);

  const paymentStats = useMemo(() => {
    const map = new Map<string, { method: string; count: number; revenue: number }>();
    uniqueInvoices.forEach(rows => {
      const method = (rows[0].paymentMethodName || 'Không rõ').trim();
      if (!map.has(method)) map.set(method, { method, count: 0, revenue: 0 });
      const s = map.get(method)!;
      s.count   += 1;
      s.revenue += rows[0].totalAmount || 0;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [uniqueInvoices]);

  const revenueByDay = useMemo(() => {
    const map = new Map<string, { date: string; revenue: number; invoiceCount: number; guestCount: number }>();
    uniqueInvoices.forEach(rows => {
      const date = rows[0].checkInDate || rows[0].checkOutDate || '-';
      if (date === '-') return;
      if (!map.has(date)) map.set(date, { date, revenue: 0, invoiceCount: 0, guestCount: 0 });
      const s = map.get(date)!;
      s.revenue      += rows[0].totalAmount || 0;
      s.invoiceCount += 1;
      s.guestCount   += rows[0].guestCount || 0;
    });
    return Array.from(map.values()).sort((a, b) => {
      const da = parseVnDateTime(a.date), db = parseVnDateTime(b.date);
      return da && db ? da.valueOf() - db.valueOf() : 0;
    });
  }, [uniqueInvoices]);
  const maxDayRevenue = Math.max(...revenueByDay.map(d => d.revenue), 1);

  const hourStats = useMemo(() => {
    const map: Record<number, { hour: number; invoiceCount: number; revenue: number }> = {};
    for (let h = 0; h < 24; h++) map[h] = { hour: h, invoiceCount: 0, revenue: 0 };
    uniqueInvoices.forEach(rows => {
      const dt = parseVnDateTime(rows[0].checkInDate, rows[0].checkInTime);
      if (!dt) return;
      const h = dt.hour();
      map[h].invoiceCount += 1;
      map[h].revenue      += rows[0].totalAmount || 0;
    });
    return Object.values(map).filter(h => h.invoiceCount > 0);
  }, [uniqueInvoices]);
  const maxHourCount = Math.max(...hourStats.map(h => h.invoiceCount), 1);

  const totalRevenue         = useMemo(() => Array.from(uniqueInvoices.values()).reduce((s, r) => s + (r[0].totalAmount || 0), 0), [uniqueInvoices]);
  const totalInvoiceCount    = uniqueInvoices.size;
  const totalGuests          = useMemo(() => Array.from(uniqueInvoices.values()).reduce((s, r) => s + (r[0].guestCount || 0), 0), [uniqueInvoices]);
  const totalItems           = useMemo(() => filtered.reduce((s, inv) => s + (inv.quantity || 0), 0), [filtered]);
  const avgRevenuePerInvoice = totalInvoiceCount > 0 ? Math.round(totalRevenue / totalInvoiceCount) : 0;
  const avgGuestPerInvoice   = totalInvoiceCount > 0 ? (totalGuests / totalInvoiceCount).toFixed(1) : '0';

  const trendTag = (pct: number) => {
    if (pct >= 70) return <Tag color="blue">Rất cao</Tag>;
    if (pct >= 40) return <Tag color="geekblue">Cao</Tag>;
    if (pct >= 15) return <Tag color="default">Trung bình</Tag>;
    return <Tag>Thấp</Tag>;
  };

  const handleExport = () => {
    const dateRangeLabel = dateRange?.[0]
      ? `${dateRange[0].format('DD/MM/YYYY')} – ${dateRange[1]?.format('DD/MM/YYYY') ?? 'hôm nay'}`
      : 'Toàn bộ dữ liệu';

    exportStatisticsToExcel({
      totalInvoiceCount,
      totalRevenue,
      avgRevenuePerInvoice,
      totalGuests,
      avgGuestPerInvoice,
      totalItems,
      dateRangeLabel,
      revenueByDay,
      itemStatsByMost,
      optionStats,
      paymentStats,
      hourStats,
    });
  };

  return (
    <div style={{ paddingBottom: 32 }}>

      {/* Modal chi tiết món */}
      <ItemDetailModal
        item={detailItem}
        allItemsCount={itemStatsByMost.length}
        maxQtyInList={maxQty}
        onClose={() => setDetailItem(null)}
      />

      {/* ── Bộ lọc ── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
        background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8,
        padding: '12px 16px', marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}>
        <Space size={8}>
          <Text style={{ color: MUTED, fontSize: 13 }}>Khoảng thời gian:</Text>
          <RangePicker
            format="DD/MM/YYYY"
            allowClear
            placeholder={['Từ ngày', 'Đến ngày']}
            value={dateRange}
            disabledDate={cur => !!cur && cur.isAfter(dayjs().endOf('day'))}
            onChange={dates => setDateRange((dates as [Dayjs | null, Dayjs | null]) || null)}
          />
        </Space>
        {dateRange?.[0] && (
          <Tag color="blue" style={{ margin: 0 }}>
            {dateRange[0].format('DD/MM/YYYY')} — {dateRange[1]?.format('DD/MM/YYYY') ?? 'hôm nay'}
          </Tag>
        )}
        <Text style={{ fontSize: 12, color: MUTED, marginLeft: 'auto' }}>
          Bấm vào món bất kỳ để xem chi tiết và option
        </Text>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          disabled={totalInvoiceCount === 0}
          style={{ marginLeft: 8 }}
        >
          Xuất báo cáo Excel
        </Button>
      </div>

      {/* ── Modal: Tổng hóa đơn ── */}
      <Modal
        open={invoiceModalOpen}
        onCancel={() => setInvoiceModalOpen(false)}
        footer={null}
        width={900}
        title={
          <Space size={8}>
            <span style={{ color: PRIMARY, fontSize: 16 }}><FileTextOutlined /></span>
            <Text strong style={{ fontSize: 15 }}>Chi tiết hóa đơn — {totalInvoiceCount} hóa đơn</Text>
          </Space>
        }
        styles={{ body: { padding: '16px 24px 24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Row gutter={[16, 16]}>
          {/* PTTT */}
          <Col xs={24} md={12}>
            <Card
              title={<SectionTitle icon={<CreditCardOutlined />} text="Phân bổ phương thức thanh toán" />}
              style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              {paymentStats.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>Không có dữ liệu</Text>
              ) : (
                paymentStats.map((pm, idx) => {
                  const pct = totalInvoiceCount > 0 ? Math.round((pm.count / totalInvoiceCount) * 100) : 0;
                  return (
                    <div key={pm.method} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <Space size={8}>
                          <Text strong style={{ fontSize: 13 }}>{pm.method}</Text>
                          <Tag color={idx === 0 ? 'blue' : 'default'} style={{ margin: 0, fontSize: 11 }}>{pct}%</Tag>
                        </Space>
                        <Space size={12}>
                          <Text style={{ fontSize: 13, color: '#262626' }}>{pm.count} HĐ</Text>
                          <Text style={{ fontSize: 12, color: MUTED }}>{fmtMoney(pm.revenue)}</Text>
                        </Space>
                      </div>
                      <Progress
                        percent={pct} showInfo={false}
                        strokeColor={idx === 0 ? PRIMARY : RANK_COLORS[Math.min(idx, RANK_COLORS.length - 1)]}
                        trailColor={BORDER} size="small"
                      />
                    </div>
                  );
                })
              )}
            </Card>
          </Col>

          {/* Doanh thu theo ngày */}
          <Col xs={24} md={12}>
            <Card
              title={<SectionTitle icon={<BarChartOutlined />} text="Doanh thu theo ngày" />}
              style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              {revenueByDay.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>Không có dữ liệu</Text>
              ) : (
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {revenueByDay.map(day => (
                    <div key={day.date} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                        <Text strong style={{ fontSize: 13 }}>{day.date}</Text>
                        <Space size={12}>
                          <Text style={{ fontSize: 12, color: MUTED }}>{day.invoiceCount} HĐ · {day.guestCount} khách</Text>
                          <Text strong style={{ fontSize: 13, color: PRIMARY }}>{fmtMoney(day.revenue)}</Text>
                        </Space>
                      </div>
                      <Progress percent={Math.round((day.revenue / maxDayRevenue) * 100)}
                        showInfo={false} strokeColor={PRIMARY} trailColor={BORDER} size="small" />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>

          {/* Khung giờ cao điểm */}
          <Col xs={24}>
            <Card
              title={<SectionTitle icon={<ClockCircleOutlined />} text="Khung giờ cao điểm" />}
              style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              {hourStats.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>Không có dữ liệu</Text>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={14}>
                    <Text style={{ fontSize: 12, color: MUTED, display: 'block', marginBottom: 8 }}>
                      Màu đậm = nhiều hóa đơn hơn. Di chuột vào ô để xem chi tiết.
                    </Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {hourStats.map(h => {
                        const intensity = h.invoiceCount / maxHourCount;
                        const alpha = 0.08 + intensity * 0.88;
                        const textColor = intensity > 0.55 ? '#fff' : '#262626';
                        return (
                          <div
                            key={h.hour}
                            title={`${String(h.hour).padStart(2, '0')}:00 — ${h.invoiceCount} HĐ — ${fmtMoney(h.revenue)}`}
                            style={{
                              width: 52, height: 52, borderRadius: 6,
                              background: `rgba(24,144,255,${alpha})`,
                              border: intensity > 0.75 ? `1.5px solid ${PRIMARY}` : `1px solid ${BORDER}`,
                              display: 'flex', flexDirection: 'column', alignItems: 'center',
                              justifyContent: 'center', cursor: 'default', transition: 'transform 0.15s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.08)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)'; }}
                          >
                            <Text style={{ fontSize: 11, color: textColor, lineHeight: '1.1' }}>
                              {String(h.hour).padStart(2, '0')}h
                            </Text>
                            <Text style={{ fontSize: 12, fontWeight: 700, color: textColor, lineHeight: '1.3' }}>
                              {h.invoiceCount}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                  <Col xs={24} md={10}>
                    <Text style={{ fontSize: 12, color: MUTED, display: 'block', marginBottom: 8 }}>Top 5 giờ bận nhất</Text>
                    <Table
                      size="small" pagination={false} className="admin-table"
                      dataSource={[...hourStats].sort((a, b) => b.invoiceCount - a.invoiceCount).slice(0, 5).map((h, i) => ({ ...h, key: i }))}
                      columns={[
                        { title: '#', render: (_: any, __: any, i: number) => <RankBadge rank={i + 1} />, width: 44 },
                        { title: 'Giờ', dataIndex: 'hour', width: 65, render: (v: number) => <Text strong style={{ fontSize: 13 }}>{String(v).padStart(2, '0')}:00</Text> },
                        { title: 'Số HĐ', dataIndex: 'invoiceCount', width: 70, render: (v: number) => <Text strong style={{ color: PRIMARY }}>{v}</Text> },
                        { title: 'Doanh thu', dataIndex: 'revenue', render: (v: number) => <Text style={{ fontSize: 12 }}>{fmtMoney(v)}</Text> },
                      ]}
                    />
                  </Col>
                </Row>
              )}
            </Card>
          </Col>
        </Row>
      </Modal>

      {/* ── KPI Cards ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {/* Tổng hóa đơn — clickable */}
        <Col xs={12} sm={8} md={4}>
          <Card
            onClick={() => setInvoiceModalOpen(true)}
            style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', cursor: 'pointer' }}
            styles={{ body: { padding: '16px 14px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ color: PRIMARY, fontSize: 16 }}><FileTextOutlined /></span>
              <Text style={{ fontSize: 12, color: MUTED }}>Tổng hóa đơn</Text>
              <Tooltip title="Bấm để xem phân tích chi tiết">
                <InfoCircleOutlined style={{ color: MUTED, fontSize: 11, marginLeft: 'auto' }} />
              </Tooltip>
            </div>
            <Statistic
              value={totalInvoiceCount}
              valueStyle={{ color: PRIMARY, fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}
            />
          </Card>
        </Col>
        {([
          { title: 'Doanh thu',      value: totalRevenue,         icon: <RiseOutlined />,    color: SUCCESS, fmt: (v: any) => Number(v).toLocaleString('vi-VN'), suffix: '₫'       },
          { title: 'TB / hóa đơn',  value: avgRevenuePerInvoice, icon: <BarChartOutlined />,color: WARNING, fmt: (v: any) => Number(v).toLocaleString('vi-VN'), suffix: '₫'       },
          { title: 'Tổng khách',     value: totalGuests,          icon: <TeamOutlined />,    color: PRIMARY, fmt: undefined,                                    suffix: undefined },
          { title: 'TB khách / HĐ', value: avgGuestPerInvoice,   icon: <TeamOutlined />,    color: MUTED,   fmt: undefined,                                    suffix: undefined },
          { title: 'Tổng lượt món', value: totalItems,           icon: <CoffeeOutlined />,  color: DANGER,  fmt: undefined,                                    suffix: undefined },
        ] as const).map(kpi => (
          <Col xs={12} sm={8} md={4} key={kpi.title}>
            <Card style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }} styles={{ body: { padding: '16px 14px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ color: kpi.color, fontSize: 16 }}>{kpi.icon}</span>
                <Text style={{ fontSize: 12, color: MUTED }}>{kpi.title}</Text>
              </div>
              <Statistic
                value={kpi.value}
                formatter={kpi.fmt as any}
                suffix={kpi.suffix}
                valueStyle={{ color: kpi.color, fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Nội dung chính ── */}
      <Row gutter={[12, 12]}>

        {/* Top món nhiều nhất */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space size={8}>
                <SectionTitle icon={<ArrowUpOutlined />} text="Món được gọi nhiều nhất" />
                <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{itemStatsByMost.length} món</Tag>
              </Space>
            }
            extra={<Text style={{ fontSize: 12, color: MUTED }}>Bấm để xem chi tiết</Text>}
            style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <InfiniteBarList
              allItems={itemStatsByMost}
              maxQty={maxQty}
              strokeColor={DANGER}
              firstBadgeText="Cao nhất"
              firstBadgeColor="red"
              height={420}
              onItemClick={setDetailItem}
            />
          </Card>
        </Col>

        {/* Top món ít nhất */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space size={8}>
                <SectionTitle icon={<ArrowDownOutlined />} text="Món được gọi ít nhất" />
                <Tag style={{ margin: 0, fontSize: 11 }}>{itemStatsByLeast.length} món</Tag>
              </Space>
            }
            extra={<Text style={{ fontSize: 12, color: MUTED }}>Bấm để xem chi tiết</Text>}
            style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <InfiniteBarList
              allItems={itemStatsByLeast}
              maxQty={bottomMaxQty}
              strokeColor={MUTED}
              firstBadgeText="Ít nhất"
              firstBadgeColor="default"
              height={420}
              onItemClick={setDetailItem}
            />
          </Card>
        </Col>

        {/* Top option toàn quán — nhiều nhất */}
        <Col xs={24} lg={12}>
          <Card
            title={<SectionTitle icon={<TrophyOutlined />} text="Top option được chọn nhiều nhất" />}
            style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            {optionStats.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 13 }}>Không có dữ liệu option</Text>
            ) : (
              <Table
                size="small" pagination={false} scroll={{ y: 260 }}
                className="admin-table"
                dataSource={optionStats.slice(0, 20).map((opt, i) => ({ ...opt, key: i }))}
                columns={[
                  { title: '#', render: (_: any, __: any, i: number) => <RankBadge rank={i + 1} />, width: 44 },
                  {
                    title: 'Option', dataIndex: 'optionName',
                    render: (v: string, _: any, i: number) => (
                      <Space size={6}>
                        <Text style={{ fontSize: 13 }}>{v}</Text>
                        {i === 0 && <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>Phổ biến nhất</Tag>}
                      </Space>
                    ),
                  },
                  {
                    title: 'Món', dataIndex: 'itemName',
                    render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>,
                  },
                  {
                    title: 'Lượt', dataIndex: 'totalQty', width: 65,
                    render: (v: number) => <Text strong style={{ color: PRIMARY }}>{v}</Text>,
                  },
                ]}
              />
            )}
          </Card>
        </Col>

        {/* Top option toàn quán — ít nhất */}
        <Col xs={24} lg={12}>
          <Card
            title={<SectionTitle icon={<FallOutlined />} text="Top option được chọn ít nhất" />}
            style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            {optionStats.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 13 }}>Không có dữ liệu option</Text>
            ) : (
              <Table
                size="small" pagination={false} scroll={{ y: 260 }}
                className="admin-table"
                dataSource={[...optionStats].reverse().slice(0, 20).map((opt, i) => ({ ...opt, key: i }))}
                columns={[
                  { title: '#', render: (_: any, __: any, i: number) => <RankBadge rank={i + 1} />, width: 44 },
                  {
                    title: 'Option', dataIndex: 'optionName',
                    render: (v: string, _: any, i: number) => (
                      <Space size={6}>
                        <Text style={{ fontSize: 13 }}>{v}</Text>
                        {i === 0 && <Tag style={{ margin: 0, fontSize: 11 }}>Ít nhất</Tag>}
                      </Space>
                    ),
                  },
                  {
                    title: 'Món', dataIndex: 'itemName',
                    render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>,
                  },
                  {
                    title: 'Lượt', dataIndex: 'totalQty', width: 65,
                    render: (v: number) => <Text style={{ fontSize: 13, color: MUTED }}>{v}</Text>,
                  },
                ]}
              />
            )}
          </Card>
        </Col>

        {/* Bảng xếp hạng toàn bộ món */}
        <Col xs={24}>
          <Card
            title={
              <Space size={8}>
                <SectionTitle icon={<FileTextOutlined />} text="Bảng xếp hạng toàn bộ món" />
                <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{itemStatsByMost.length} món</Tag>
              </Space>
            }
            extra={<Text style={{ fontSize: 12, color: MUTED }}>Bấm vào hàng để xem chi tiết</Text>}
            style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              size="small" className="admin-table"
              pagination={{ pageSize: 15, showSizeChanger: true, showTotal: t => `${t} món` }}
              dataSource={itemStatsByMost.map((item, i) => ({ ...item, key: item.itemName, rank: i + 1 }))}
              onRow={record => ({ onClick: () => setDetailItem(record as unknown as ItemStat), style: { cursor: 'pointer' } })}
              rowClassName={() => 'stats-table-clickable-row'}
              columns={[
                { title: '#', dataIndex: 'rank', width: 50, render: (v: number) => <RankBadge rank={v} /> },
                {
                  title: 'Tên món', dataIndex: 'itemName',
                  render: (v: string, _: any, i: number) => (
                    <Space size={6}>
                      <Text strong style={{ fontSize: 13 }}>{v}</Text>
                      {i === 0 && <Tag color="red" style={{ margin: 0, fontSize: 11 }}>Cao nhất</Tag>}
                      {i === itemStatsByMost.length - 1 && itemStatsByMost.length > 1 && (
                        <Tag style={{ margin: 0, fontSize: 11 }}>Thấp nhất</Tag>
                      )}
                    </Space>
                  ),
                },
                {
                  title: 'Lượt gọi', dataIndex: 'totalQty',
                  sorter: (a: any, b: any) => a.totalQty - b.totalQty,
                  defaultSortOrder: 'descend' as const, width: 100,
                  render: (v: number) => <Text strong style={{ color: PRIMARY }}>{v}</Text>,
                },
                {
                  title: 'Xuất hiện trong HĐ', dataIndex: 'invoiceCount', width: 150,
                  sorter: (a: any, b: any) => a.invoiceCount - b.invoiceCount,
                  render: (v: number) => v,
                },
                {
                  title: 'Doanh thu', dataIndex: 'totalRevenue', width: 160,
                  sorter: (a: any, b: any) => a.totalRevenue - b.totalRevenue,
                  render: (v: number) => fmtMoney(v),
                },
                {
                  title: 'TB / lần gọi', width: 130,
                  render: (_: any, record: any) =>
                    record.totalQty > 0 ? fmtMoney(Math.round(record.totalRevenue / record.totalQty)) : '-',
                },
                {
                  title: 'Số loại option', width: 110,
                  render: (_: any, record: any) => Object.keys(record.options).length,
                },
                {
                  title: 'Xu hướng', width: 110,
                  render: (_: any, record: any) => {
                    const pct = Math.round((record.totalQty / (itemStatsByMost[0]?.totalQty || 1)) * 100);
                    return trendTag(pct);
                  },
                },
              ]}
            />
          </Card>
        </Col>

      </Row>
    </div>
  );
};

export default InvoiceStatistics;
